import type { EmitterAnchor, EmitterConfig, ParticleCpu } from "./types";
import {
  PARTICLE_INSTANCE_FLOATS,
  PARTICLE_MAX_INSTANCES,
  evalCurve,
  presetAfterCoolingSmoke,
  presetDustPuff,
  presetEmbers,
  presetFallingSmokeTrail,
  presetImpactSparks,
  presetShellStripFire,
  presetShellStripSmoke,
  presetWallImpactSmoke,
  presetWallSustainFire,
} from "./types";

export interface EmitterRegistration {
  anchor: EmitterAnchor;
  config: EmitterConfig;
}

export interface HeatSource {
  x: number;
  y: number;
  z: number;
}

interface EmitterSpawnState {
  carry: number;
  burstDone: boolean;
}

interface WallBucket {
  n: number;
  lastSec: number;
  wx: number;
  wy: number;
  wz: number;
  emberCarry: number;
}

function normalize3(x: number, y: number, z: number): [number, number, number] {
  const l = Math.hypot(x, y, z);
  if (l < 1e-8) return [0, 0, 1];
  return [x / l, y / l, z / l];
}

/** right, up для базиса с осью forward (направление конуса). */
function orthonormalBasis(fx: number, fy: number, fz: number): {
  rx: number; ry: number; rz: number;
  ux: number; uy: number; uz: number;
} {
  let ax = 0, ay = 0, az = 1;
  if (Math.abs(fx * ax + fy * ay + fz * az) > 0.95) {
    ax = 0;
    ay = 1;
    az = 0;
  }
  let rx = ay * fz - az * fy;
  let ry = az * fx - ax * fz;
  let rz = ax * fy - ay * fx;
  let rl = Math.hypot(rx, ry, rz);
  if (rl < 1e-8) {
    rx = 1;
    ry = 0;
    rz = 0;
    rl = 1;
  }
  rx /= rl;
  ry /= rl;
  rz /= rl;
  let ux = fy * rz - fz * ry;
  let uy = fz * rx - fx * rz;
  let uz = fx * ry - fy * rx;
  const ul = Math.hypot(ux, uy, uz) || 1;
  ux /= ul;
  uy /= ul;
  uz /= ul;
  return { rx, ry, rz, ux, uy, uz };
}

function sampleConeLocal(coneRad: number): [number, number, number] {
  const u = Math.random();
  const cosTheta = 1 - u * (1 - Math.cos(coneRad));
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
  const phi = Math.random() * Math.PI * 2;
  const lx = sinTheta * Math.cos(phi);
  const ly = sinTheta * Math.sin(phi);
  const lz = cosTheta;
  return [lx, ly, lz];
}

function worldDirFromEmitConfig(cfg: EmitterConfig): [number, number, number] {
  const [dx, dy, dz] = cfg.emitDir;
  const [fx, fy, fz] = normalize3(dx, dy, dz);
  const { rx, ry, rz, ux, uy, uz } = orthonormalBasis(fx, fy, fz);
  const [lx, ly, lz] = sampleConeLocal(cfg.coneAngleRad);
  const wx = lx * rx + ly * ux + lz * fx;
  const wy = lx * ry + ly * uy + lz * fy;
  const wz = lx * rz + ly * uz + lz * fz;
  return normalize3(wx, wy, wz);
}

function worldDirFromForwardCone(
  fx: number, fy: number, fz: number,
  coneRad: number,
): [number, number, number] {
  const [hx, hy, hz] = normalize3(fx, fy, fz);
  const { rx, ry, rz, ux, uy, uz } = orthonormalBasis(hx, hy, hz);
  const [lx, ly, lz] = sampleConeLocal(coneRad);
  const wx = lx * rx + ly * ux + lz * hx;
  const wy = lx * ry + ly * uy + lz * hy;
  const wz = lx * rz + ly * uz + lz * hz;
  return normalize3(wx, wy, wz);
}

function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class ParticleSimulation {
  private particles: ParticleCpu[] = [];
  private emitters: EmitterRegistration[] = [];
  private spawnState: EmitterSpawnState[] = [];
  private nextSeed = 1;
  private shellFxFireId = -1;
  private shellFxSmokeId = -1;
  private wallImpactSmokeId = -1;
  private wallSustainFireId = -1;
  private impactSparkId = -1;
  private dustPuffId = -1;
  private afterCoolingSmokeId = -1;
  private emberId = -1;
  private fallingTrailSmokeId = -1;

  /** Квант позиции попадания → счётчик для «огня после >5 выстрелов». */
  private wallSpotBuckets = new Map<string, WallBucket>();
  private readonly wallSpotQuant = 0.22;
  readonly wallSpotDecaySec = 3.2;

  registerEmitter(config: EmitterConfig, anchor: EmitterAnchor): number {
    const id = this.emitters.length;
    this.emitters.push({ anchor: { ...anchor }, config });
    this.spawnState.push({ carry: 0, burstDone: false });
    return id;
  }

  setEmitterAnchor(id: number, x: number, y: number, z: number): void {
    const e = this.emitters[id];
    if (!e) return;
    e.anchor.x = x;
    e.anchor.y = y;
    e.anchor.z = z;
  }

  setEmitterEnabled(id: number, on: boolean): void {
    const e = this.emitters[id];
    if (!e) return;
    e.config.enabled = on;
  }

  getEmitterCount(): number {
    return this.emitters.length;
  }

  /** До 4 активных точек sustain-fire для heat-haze (desktop). */
  getHeatSources(_timeSec: number): HeatSource[] {
    const out: HeatSource[] = [];
    for (const b of this.wallSpotBuckets.values()) {
      if (b.n > 5 && out.length < 4) {
        out.push({ x: b.wx, y: b.wy, z: b.wz });
      }
    }
    return out;
  }

  private ensureShellFxEmitters(): void {
    if (this.shellFxFireId >= 0) return;
    this.shellFxFireId = this.registerEmitter(presetShellStripFire(), { x: 0, y: 0, z: 0 });
    this.shellFxSmokeId = this.registerEmitter(presetShellStripSmoke(), { x: 0, y: 0, z: 0 });
  }

  private ensureWallFxEmitters(): void {
    if (this.wallImpactSmokeId >= 0) return;
    this.wallImpactSmokeId = this.registerEmitter(presetWallImpactSmoke(), { x: 0, y: 0, z: 0 });
    this.wallSustainFireId = this.registerEmitter(presetWallSustainFire(), { x: 0, y: 0, z: 0 });
  }

  private ensureImpactSparkEmitter(): void {
    if (this.impactSparkId >= 0) return;
    this.impactSparkId = this.registerEmitter(presetImpactSparks(), { x: 0, y: 0, z: 0 });
  }

  private ensureDustPuffEmitter(): void {
    if (this.dustPuffId >= 0) return;
    this.dustPuffId = this.registerEmitter(presetDustPuff(), { x: 0, y: 0, z: 0 });
  }

  private ensureAfterCoolingEmitter(): void {
    if (this.afterCoolingSmokeId >= 0) return;
    this.afterCoolingSmokeId = this.registerEmitter(presetAfterCoolingSmoke(), { x: 0, y: 0, z: 0 });
  }

  private ensureEmberEmitter(): void {
    if (this.emberId >= 0) return;
    this.emberId = this.registerEmitter(presetEmbers(), { x: 0, y: 0, z: 0 });
  }

  private ensureFallingTrailEmitter(): void {
    if (this.fallingTrailSmokeId >= 0) return;
    this.fallingTrailSmokeId = this.registerEmitter(presetFallingSmokeTrail(), { x: 0, y: 0, z: 0 });
  }

  /** Удаляет самые старые частицы с указанными emitterId, чтобы новый burst всегда мог добавиться. */
  private evictOldestFromEmitters(emitterIds: Set<number>, removeCount: number): void {
    if (removeCount <= 0) return;
    type Item = { idx: number; born: number };
    const items: Item[] = [];
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i]!;
      if (emitterIds.has(p.emitterId)) items.push({ idx: i, born: p.born });
    }
    items.sort((a, b) => a.born - b.born || a.idx - b.idx);
    const kill = new Set<number>();
    for (let k = 0; k < Math.min(removeCount, items.length); k++) {
      kill.add(items[k]!.idx);
    }
    let w = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (kill.has(i)) continue;
      if (w !== i) this.particles[w] = this.particles[i]!;
      w++;
    }
    this.particles.length = w;
  }

  /** Если пул забит — выкидываем самые старые частицы (любые), чтобы не обрывать FX при полном буфере. */
  private evictOldestGlobal(removeCount: number): void {
    if (removeCount <= 0) return;
    const ord = this.particles
      .map((p, i) => ({ i, born: p.born }))
      .sort((a, b) => a.born - b.born || a.i - b.i);
    const kill = new Set(ord.slice(0, Math.min(removeCount, ord.length)).map((x) => x.i));
    let w = 0;
    for (let i = 0; i < this.particles.length; i++) {
      if (kill.has(i)) continue;
      if (w !== i) this.particles[w] = this.particles[i]!;
      w++;
    }
    this.particles.length = w;
  }

  /** Освобождение места под burst попаданий в стены (отдельные emitter id от shell FX). */
  private makeRoomForWallBurst(need: number): void {
    let shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    if (shortage <= 0) return;
    const ids = new Set<number>();
    if (this.wallImpactSmokeId >= 0) ids.add(this.wallImpactSmokeId);
    if (this.wallSustainFireId >= 0) ids.add(this.wallSustainFireId);
    if (this.impactSparkId >= 0) ids.add(this.impactSparkId);
    if (ids.size > 0) {
      this.evictOldestFromEmitters(ids, shortage);
      shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    }
    if (shortage > 0) {
      this.evictOldestGlobal(shortage);
    }
  }

  /** Гарантирует место под новый shell-burst: сначала старый огонь/дым этого FX, затем при необходимости любые частицы. */
  private makeRoomForShellBurst(need: number): void {
    let shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    if (shortage <= 0) return;
    const fj = this.shellFxFireId;
    const sj = this.shellFxSmokeId;
    if (fj >= 0 && sj >= 0) {
      this.evictOldestFromEmitters(new Set([fj, sj]), shortage);
      shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    }
    if (shortage > 0) {
      this.evictOldestGlobal(shortage);
    }
  }

  private makeRoomForMiscBurst(need: number, emitterIds: number[]): void {
    let shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    if (shortage <= 0) return;
    const s = new Set(emitterIds.filter((id) => id >= 0));
    if (s.size > 0) {
      this.evictOldestFromEmitters(s, shortage);
      shortage = this.particles.length + need - PARTICLE_MAX_INSTANCES;
    }
    if (shortage > 0) {
      this.evictOldestGlobal(shortage);
    }
  }

  private pushManualParticle(
    emitterId: number,
    ox: number,
    oy: number,
    oz: number,
    timeSec: number,
  ): void {
    if (this.particles.length >= PARTICLE_MAX_INSTANCES) return;
    const reg = this.emitters[emitterId];
    if (!reg) return;
    const cfg = reg.config;
    const rnd = mulberry32(this.nextSeed++);
    const lifetime =
      cfg.lifetimeMin + rnd() * (cfg.lifetimeMax - cfg.lifetimeMin);
    const speed = cfg.speedMin + rnd() * (cfg.speedMax - cfg.speedMin);
    const [wx, wy, wz] = worldDirFromEmitConfig(cfg);
    const vx = wx * speed;
    const vy = wy * speed;
    const vz = wz * speed;
    this.particles.push({
      x: ox + cfg.offset[0],
      y: oy + cfg.offset[1],
      z: oz + cfg.offset[2],
      vx,
      vy,
      vz,
      born: timeSec,
      lifetime,
      seed: rnd() * 1e6,
      emitterId,
    });
  }

  /** Одна частица с направлением конуса вокруг forward (мировые координаты). */
  private pushDirectedParticle(
    emitterId: number,
    ox: number,
    oy: number,
    oz: number,
    timeSec: number,
    forwardX: number,
    forwardY: number,
    forwardZ: number,
  ): void {
    if (this.particles.length >= PARTICLE_MAX_INSTANCES) return;
    const reg = this.emitters[emitterId];
    if (!reg) return;
    const cfg = reg.config;
    const rnd = mulberry32(this.nextSeed++);
    const lifetime =
      cfg.lifetimeMin + rnd() * (cfg.lifetimeMax - cfg.lifetimeMin);
    const speed = cfg.speedMin + rnd() * (cfg.speedMax - cfg.speedMin);
    const [wx, wy, wz] = worldDirFromForwardCone(forwardX, forwardY, forwardZ, cfg.coneAngleRad);
    const vx = wx * speed;
    const vy = wy * speed;
    const vz = wz * speed;
    this.particles.push({
      x: ox + cfg.offset[0],
      y: oy + cfg.offset[1],
      z: oz + cfg.offset[2],
      vx,
      vy,
      vz,
      born: timeSec,
      lifetime,
      seed: rnd() * 1e6,
      emitterId,
    });
  }

  /**
   * Попадание в геометрию уровня: дым каждый раз; если в ту же квантованную точку >5 попаданий подряд — добавляется огонь.
   */
  recordWallGeometryHit(wx: number, wy: number, wz: number, timeSec: number): void {
    const q = this.wallSpotQuant;
    const ix = Math.round(wx / q);
    const iy = Math.round(wy / q);
    const iz = Math.round(wz / q);
    const key = `${ix},${iy},${iz}`;
    let b = this.wallSpotBuckets.get(key);
    if (!b || timeSec - b.lastSec > this.wallSpotDecaySec) {
      b = { n: 0, lastSec: timeSec, wx, wy, wz, emberCarry: 0 };
    }
    b.n++;
    b.lastSec = timeSec;
    b.wx = wx;
    b.wy = wy;
    b.wz = wz;
    this.wallSpotBuckets.set(key, b);
    const sustainFire = b.n > 5;
    this.spawnWallImpactBurst(wx, wy, wz, timeSec, sustainFire);
  }

  private spawnWallImpactBurst(
    wx: number,
    wy: number,
    wz: number,
    timeSec: number,
    sustainFire: boolean,
  ): void {
    this.ensureWallFxEmitters();
    const sm = this.wallImpactSmokeId;
    const fi = this.wallSustainFireId;
    const nSmoke = sustainFire ? 34 : 17;
    const nFire = sustainFire ? 52 : 0;
    this.makeRoomForWallBurst(nSmoke + nFire);
    for (let i = 0; i < nSmoke; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.26;
      const oy = wy + (Math.random() - 0.5) * 0.26;
      const oz = wz + (Math.random() - 0.5) * 0.32;
      this.pushManualParticle(sm, ox, oy, oz, timeSec);
    }
    for (let i = 0; i < nFire; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.22;
      const oy = wy + (Math.random() - 0.5) * 0.22;
      const oz = wz + (Math.random() - 0.5) * 0.28;
      this.pushManualParticle(fi, ox, oy, oz, timeSec);
    }
  }

  /** Искры: spray назад вдоль bulletRayDir (от стартa к попаданию) или по импульсу куба. */
  spawnImpactSparks(
    wx: number,
    wy: number,
    wz: number,
    timeSec: number,
    dirX: number,
    dirY: number,
    dirZ: number,
    count: number,
  ): void {
    this.ensureImpactSparkEmitter();
    const id = this.impactSparkId;
    if (id < 0) return;
    let fx = -dirX;
    let fy = -dirY;
    let fz = -dirZ;
    const ll = Math.hypot(fx, fy, fz);
    if (ll < 1e-4) {
      fx = 0;
      fy = 0;
      fz = 1;
    } else {
      fx /= ll;
      fy /= ll;
      fz /= ll;
    }
    this.makeRoomForMiscBurst(count, [id]);
    for (let i = 0; i < count; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.08;
      const oy = wy + (Math.random() - 0.5) * 0.08;
      const oz = wz + (Math.random() - 0.5) * 0.1;
      this.pushDirectedParticle(id, ox, oy, oz, timeSec, fx, fy, fz);
    }
  }

  /** Пыль при ударе осколка о пол. */
  spawnDustPuff(
    wx: number,
    wy: number,
    wz: number,
    timeSec: number,
    energy: number,
    lite: boolean,
  ): void {
    this.ensureDustPuffEmitter();
    const id = this.dustPuffId;
    if (id < 0) return;
    const n = lite ? 2 : 4 + Math.min(2, Math.floor(energy * 2));
    this.makeRoomForMiscBurst(n, [id]);
    const fz = 0.65 + energy * 0.35;
    for (let i = 0; i < n; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.12;
      const oy = wy + (Math.random() - 0.5) * 0.12;
      const oz = wz + (Math.random() - 0.5) * 0.06;
      this.pushDirectedParticle(id, ox, oy, oz, timeSec, 0, 0, fz);
    }
  }

  /** Дым следа за крупной falling group. */
  spawnFallingSmokeTrail(wx: number, wy: number, wz: number, timeSec: number): void {
    this.ensureFallingTrailEmitter();
    const id = this.fallingTrailSmokeId;
    if (id < 0) return;
    const n = 4;
    this.makeRoomForMiscBurst(n, [id]);
    for (let i = 0; i < n; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.35;
      const oy = wy + (Math.random() - 0.5) * 0.35;
      const oz = wz + (Math.random() - 0.5) * 0.25;
      this.pushManualParticle(id, ox, oy, oz, timeSec);
    }
  }

  private spawnAfterCoolingBurst(wx: number, wy: number, wz: number, timeSec: number): void {
    this.ensureAfterCoolingEmitter();
    const id = this.afterCoolingSmokeId;
    if (id < 0) return;
    const n = 8 + Math.floor(Math.random() * 5);
    this.makeRoomForMiscBurst(n, [id]);
    for (let i = 0; i < n; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.18;
      const oy = wy + (Math.random() - 0.5) * 0.18;
      const oz = wz + (Math.random() - 0.5) * 0.22;
      this.pushManualParticle(id, ox, oy, oz, timeSec);
    }
  }

  /** Дым и интенсивный огонь при снятии верхней гладкой оболочки (body shell). */
  spawnShellStripBurst(
    wx: number,
    wy: number,
    wz: number,
    timeSec: number,
    lite = false,
  ): void {
    this.ensureShellFxEmitters();
    const fj = this.shellFxFireId;
    const sj = this.shellFxSmokeId;
    const nFire = lite ? 36 : 92;
    const nSmoke = lite ? 24 : 62;
    this.makeRoomForShellBurst(nFire + nSmoke);
    for (let i = 0; i < nFire; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.28;
      const oy = wy + (Math.random() - 0.5) * 0.28;
      const oz = wz + (Math.random() - 0.5) * 0.35;
      this.pushManualParticle(fj, ox, oy, oz, timeSec);
    }
    for (let i = 0; i < nSmoke; i++) {
      const ox = wx + (Math.random() - 0.5) * 0.32;
      const oy = wy + (Math.random() - 0.5) * 0.32;
      const oz = wz + (Math.random() - 0.5) * 0.42;
      this.pushManualParticle(sj, ox, oy, oz, timeSec);
    }
  }

  /** weapons: выброс count частиц эмиттера emitterId из точки (x,y,z) с
   *  разбросом jitter (м): по конусу эмиттера вокруг dir (или его emitDir),
   *  плюс скорость носителя carry (струя огнемёта летит со скоростью струи). */
  emitBurst(
    emitterId: number,
    x: number, y: number, z: number,
    timeSec: number,
    count: number,
    dir?: [number, number, number],
    jitter = 0,
    carry?: [number, number, number],
  ): void {
    if (count <= 0 || !this.emitters[emitterId]) return;
    this.makeRoomForMiscBurst(count, [emitterId]);
    for (let i = 0; i < count; i++) {
      const ox = x + (Math.random() - 0.5) * jitter;
      const oy = y + (Math.random() - 0.5) * jitter;
      const oz = z + (Math.random() - 0.5) * jitter;
      const before = this.particles.length;
      if (dir) this.pushDirectedParticle(emitterId, ox, oy, oz, timeSec, dir[0], dir[1], dir[2]);
      else this.pushManualParticle(emitterId, ox, oy, oz, timeSec);
      if (carry && this.particles.length > before) {
        const p = this.particles[this.particles.length - 1]!;
        p.vx += carry[0];
        p.vy += carry[1];
        p.vz += carry[2];
      }
    }
  }

  update(dt: number, timeSec: number, opts?: { embersEnabled?: boolean }): void {
    if (dt <= 0) return;

    const embersOn = opts?.embersEnabled !== false;

    const coolingKeys: string[] = [];
    for (const [k, b] of this.wallSpotBuckets) {
      if (timeSec - b.lastSec > this.wallSpotDecaySec) {
        if (b.n > 0) {
          this.spawnAfterCoolingBurst(b.wx, b.wy, b.wz, timeSec);
        }
        coolingKeys.push(k);
      }
    }
    for (const k of coolingKeys) {
      this.wallSpotBuckets.delete(k);
    }

    this.ensureEmberEmitter();
    const emId = this.emberId;
    if (embersOn && emId >= 0) {
      for (const b of this.wallSpotBuckets.values()) {
        if (b.n <= 5) continue;
        b.emberCarry += 8 * dt;
        let spawned = 0;
        while (b.emberCarry >= 1 && spawned < 3 && this.particles.length < PARTICLE_MAX_INSTANCES) {
          b.emberCarry -= 1;
          spawned++;
          const ox = b.wx + (Math.random() - 0.5) * 0.14;
          const oy = b.wy + (Math.random() - 0.5) * 0.14;
          const oz = b.wz + (Math.random() - 0.5) * 0.12;
          this.pushDirectedParticle(emId, ox, oy, oz, timeSec, 0, 0, 1);
        }
      }
    }

    for (let ei = 0; ei < this.emitters.length; ei++) {
      const reg = this.emitters[ei];
      const cfg = reg.config;
      if (!cfg.enabled) continue;
      const st = this.spawnState[ei];

      let toSpawn = st.carry + cfg.rate * dt;

      const burst = cfg.burst ?? 0;
      if (burst > 0 && !st.burstDone) {
        toSpawn += burst;
        st.burstDone = true;
      }

      const nSpawn = Math.floor(toSpawn);
      st.carry = toSpawn - nSpawn;

      for (let k = 0; k < nSpawn; k++) {
        if (this.particles.length >= PARTICLE_MAX_INSTANCES) break;
        const rnd = mulberry32(this.nextSeed++);
        const lifetime =
          cfg.lifetimeMin + rnd() * (cfg.lifetimeMax - cfg.lifetimeMin);
        const speed = cfg.speedMin + rnd() * (cfg.speedMax - cfg.speedMin);
        const [wx, wy, wz] = worldDirFromEmitConfig(cfg);
        const ox = reg.anchor.x + cfg.offset[0];
        const oy = reg.anchor.y + cfg.offset[1];
        const oz = reg.anchor.z + cfg.offset[2];
        const vx = wx * speed;
        const vy = wy * speed;
        const vz = wz * speed;
        this.particles.push({
          x: ox,
          y: oy,
          z: oz,
          vx,
          vy,
          vz,
          born: timeSec,
          lifetime,
          seed: rnd() * 1e6,
          emitterId: ei,
        });
      }
    }

    let write = 0;
    const dragGlobal = Math.exp(-2.5 * dt);
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      const reg = this.emitters[p.emitterId];
      if (!reg) continue;
      const cfg = reg.config;
      const age = timeSec - p.born;
      if (age >= p.lifetime) continue;

      const dMul = Math.exp(-cfg.drag * dt);
      let vx = p.vx * dMul * dragGlobal;
      let vy = p.vy * dMul * dragGlobal;
      let vz = p.vz * dMul * dragGlobal;

      if (cfg.turbulence > 1e-6) {
        const r = mulberry32((Math.floor(p.seed) ^ (p.emitterId * 9973)) >>> 0);
        vx += (r() - 0.5) * cfg.turbulence * dt * 8;
        vy += (r() - 0.5) * cfg.turbulence * dt * 8;
        vz += (r() - 0.5) * cfg.turbulence * dt * 5;
      }

      vz -= cfg.gravityZ * dt;

      const nx = p.x + vx * dt;
      const ny = p.y + vy * dt;
      const nz = p.z + vz * dt;

      if (write !== i) {
        this.particles[write] = p;
      }
      const q = this.particles[write];
      q.x = nx;
      q.y = ny;
      q.z = nz;
      q.vx = vx;
      q.vy = vy;
      q.vz = vz;
      write++;
    }
    this.particles.length = write;
  }

  packInstances(
    timeSec: number,
    eyeX: number,
    eyeY: number,
    eyeZ: number,
    outFire: Float32Array,
    outSmoke: Float32Array,
  ): { fireCount: number; smokeCount: number } {
    type SortIdx = { idx: number; d2: number };
    const fireScratch: SortIdx[] = [];
    const smokeScratch: SortIdx[] = [];

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      const reg = this.emitters[p.emitterId];
      if (!reg) continue;
      const cfg = reg.config;
      const dx = p.x - eyeX;
      const dy = p.y - eyeY;
      const dz = p.z - eyeZ;
      const d2 = dx * dx + dy * dy + dz * dz;

      if (cfg.visual === "fire" || cfg.visual === "spark") {
        fireScratch.push({ idx: i, d2 });
      } else {
        smokeScratch.push({ idx: i, d2 });
      }
    }

    smokeScratch.sort((a, b) => b.d2 - a.d2);

    const visualKindFloat = (cfg: EmitterConfig): number => {
      if (cfg.visual === "fire") return 0;
      if (cfg.visual === "smoke") return 1;
      return 2;
    };

    const writeParticle = (
      p: ParticleCpu,
      out: Float32Array,
      slot: number,
    ): void => {
      const reg = this.emitters[p.emitterId];
      const cfg = reg.config;
      const age = Math.max(0, timeSec - p.born);
      const t = Math.min(1, Math.max(0, age / p.lifetime));
      const sizeMul = evalCurve(cfg.sizeOverLife, t);
      const alphaMul = evalCurve(cfg.alphaOverLife, t);
      const size = cfg.baseSize * sizeMul;
      const vk = visualKindFloat(cfg);

      const base = slot * PARTICLE_INSTANCE_FLOATS;
      out[base] = p.x;
      out[base + 1] = p.y;
      out[base + 2] = p.z;
      out[base + 3] = size;
      out[base + 4] = t;
      out[base + 5] = p.seed;
      out[base + 6] = vk;
      out[base + 7] = alphaMul;
      out[base + 8] = timeSec;
      out[base + 9] = p.born;
      out[base + 10] = p.lifetime;
      out[base + 11] = 0;
      out[base + 12] = p.vx;
      out[base + 13] = p.vy;
      out[base + 14] = p.vz;
      out[base + 15] = 0;
    };

    let fi = 0;
    for (let s = 0; s < fireScratch.length; s++) {
      if (fi >= PARTICLE_MAX_INSTANCES) break;
      writeParticle(this.particles[fireScratch[s].idx], outFire, fi++);
    }

    let si = 0;
    for (let s = 0; s < smokeScratch.length; s++) {
      if (si >= PARTICLE_MAX_INSTANCES) break;
      writeParticle(this.particles[smokeScratch[s].idx], outSmoke, si++);
    }

    return { fireCount: fi, smokeCount: si };
  }
}
